import parseISO from "date-fns/parseISO";
import { client as request } from "./request";
import ProjectMember from "@/models/project-member";
import Permission from "@/models/permission";
import {
  getProjectStatus,
  getProjectStatusCode,
  ProjectStatus
} from "@/models/project-status";
import { ProjectPropertyKey } from "./project/project-properties-api";

interface Company {
  companyId: number;
  serviceId: number;
  name: string;
  family: boolean;
  client: boolean;
  vendor: boolean;
}

interface Office {
  officeId: number;
  companyId: number;
  name: string;
  tel1: string;
  tel2: string;
  fax: string;
  mail: string;
  postCode: string;
  address: string;
}

interface Client {
  company: Company;
  office: Office;
}

interface Staff {
  staffId: number;
  officeId: number;
  name: string;
  email: string;
}

/**
 * 現場
 */
export interface Project {
  projectId: number;
  orderId: number;
  taskName: string;
  name: string;
  icon: string;
  postcode: string;
  address: string;
  note: string;
  status: ProjectStatus;
  latitude: number;
  longitude: number;
  start: string;
  end: string;
  client: Client;
  clientStaff: Staff;
  created: Date;
  closed: boolean;
  managers: ProjectMember[];
  sales: ProjectMember[];
  sSystemId: string;
  tgId: string;
  OHANo: string;
  holdOut: boolean;
  templateId: string;
  beforeTemplateId: number;
  additionalProperties: ProjectProperty[];
}

/**
 * 受け取ったデータを現場に変換する
 * @param data JSONから作ったオブジェクト
 */
function parseProject(data: any): Project {
  // return Object.assign({}, data, { created: parseISO(data.created) });

  // TODO: managersとsalesにAPIからnullが返ってきているので、空配列を返してもらった方がいい
  return {
    ...data,
    status: getProjectStatus(data.status),
    created: parseISO(data.created),
    managers: data.managers || [],
    sales: data.sales || [],
    // 互換性のために初期値を入れる
    additionalProperties: data.additionalProperties || []
  };
}

export interface GetProjectsQuery {
  status?: ProjectStatus;
  client?: string;
  keyword?: string;
  projectCount?: number;
}

/**
 * 現場の一覧を取得する
 */
export async function getAllProjects(
  query: GetProjectsQuery = {}
): Promise<Project[]> {
  const res = await request.get("get_all_projects.php", {
    ...query,
    status: query.status && getProjectStatusCode(query.status)
  });
  return res.projectList.map(parseProject);
}

/**
 * UserがProjectMemberになっているProjectのリストを得る。主にダッシュボード用。
 */
export async function getAssignedProjects(
  query: string = "",
  status: string = "",
  projectCount?: number
): Promise<Project[]> {
  const res = await request.get("get_assigned_projects.php", {
    query,
    status,
    projectCount
  });
  // TODO: 空の時nullが返ってくるのでAPI側で空配列を返してもらった方がいい
  if (!res) return [];
  return res.projectList.map(parseProject);
}

/**
 * 指定した現場を取得する
 */
export async function getProject(projectId: any) {
  const res = await request.get("get_project.php", { projectId });
  return parseProject(res.project);
}

/**
 * Projectを保存する
 */
export async function saveProject(data: FormData) {
  const res = await request.post("save_project.php", data);
  return res.projectId;
}

/**
 * ProjectとEndUserを保存する
 */
export async function saveEndUserProject(data: FormData) {
  const res = await request.post("save_end_user_project.php", data);
  return res.projectId;
}

/**
 * 現場を削除する
 */
export async function deleteProject(projectId: any) {
  await request.post("delete_project.php", {}, { projectId });
}

/**
 * 自分が現場で持つ権限を得る
 */
export async function getProjectPermissions(projectId: any) {
  const permissions = await request.get("get_project_permissions.php", {
    projectId
  });
  return permissions as Permission[];
}

/**
 * 現場新規作成時にデフォルトのアイコンを取得
 */
export async function getDefaultIcon() {
  const res = await request.get("get_project_icon.php");
  return res.icon;
}

/**
 * csvから現場情報をインポート
 */
export async function saveProjectCsv(data: FormData) {
  await request.post("import_project_csv.php", data);
}

export async function importCsvProjectChangeStatus(data: FormData) {
  await request.post("import_csv_project_change_status.php", data);
}

export interface ConfirmProjectScheduleFormRequest {
  id: number; // project
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
}

/**
 * 現場の日付を確定する
 *
 * @deprecated 現場の編集でまとめて行うのでAPIとしては使わなくなった
 */
export async function confirmProjectSchedule(
  data: ConfirmProjectScheduleFormRequest
): Promise<void> {
  console.warn("confirmProjectSchedule is not implemented yet.", data);
}

export interface SuspendProjectCommand {
  id: number;
  pending: boolean;
}

export interface CloseProjectCommand {
  id: number;
  closed: boolean;
}

/**
 * 現場の保留を切り替える
 */
export async function suspendProject(
  command: SuspendProjectCommand
): Promise<any> {
  const d = new FormData();
  d.append("id", String(command.id));
  d.append("pending", command.pending ? "1" : "0");
  return await request.post("suspend_project.php", d);
}

export async function closeProject(command: CloseProjectCommand): Promise<any> {
  const d = new FormData();
  d.append("id", String(command.id));
  d.append("closed", command.closed ? "1" : "0");
  return await request.post("close_project.php", d);
}

export interface ProjectProperty {
  key: ProjectPropertyKey;
  value: string;
}
